import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import { setDiningOption, setLoyaltyCode, setLoyaltyProfile, setCurrentScreen, clearCart } from '../../store/orderSlice';
import Button from '../ui/Button';
import StartOverButton from '../ui/StartOverButton';
import LoyaltyImage from '../../data/PanaImages/loyaltyImg.jpg';
import BackIcon from '../../data/PanaImages/BackIcon.jpg';
import CloseIcon from '../../data/PanaImages/x.jpg';
import apiService from '../../services/apiService';
import { apiConfig } from '../../config/api';

const LoyaltyScreen: React.FC = () => {
  const dispatch = useDispatch();
  const loyaltyImage = useSelector((state: RootState) => state.order.onboardingImages.loyaltyImage);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpValue, setOtpValue] = useState('');
  const [showOtpError, setShowOtpError] = useState(false);
  const [otpErrorMessage, setOtpErrorMessage] = useState('');
  const [isSubmittingOtp, setIsSubmittingOtp] = useState(false);
  const otpSubmittedRef = useRef(false);

  const handleSkipLoyalty = () => {
    dispatch(setLoyaltyCode(null));
    dispatch(setDiningOption('eat-in'));
  };

  const handleStartOver = () => {
    dispatch(clearCart());
    dispatch(setLoyaltyCode(null));
    dispatch(setCurrentScreen('welcome'));
  };

  const handleOpenOtpModal = () => {
    setOtpValue('');
    setShowOtpError(false);
    setOtpErrorMessage('');
    otpSubmittedRef.current = false;
    setShowOtpModal(true);
  };

  const handleCloseOtpModal = () => {
    setShowOtpModal(false);
    setOtpValue('');
    setShowOtpError(false);
    setOtpErrorMessage('');
    setIsSubmittingOtp(false);
    otpSubmittedRef.current = false;
  };

  const handleOtpDigit = (digit: string) => {
    setOtpValue((prev) => {
      if (prev.length >= 6) return prev;
      return `${prev}${digit}`;
    });
  };

  const handleOtpBackspace = () => {
    setOtpValue((prev) => prev.slice(0, -1));
    setShowOtpError(false);
    setOtpErrorMessage('');
    otpSubmittedRef.current = false;
  };

  useEffect(() => {
    if (otpValue.length !== 6 || otpSubmittedRef.current || isSubmittingOtp) return;

    otpSubmittedRef.current = true;
    setIsSubmittingOtp(true);
    setShowOtpError(false);
    setOtpErrorMessage('');

    const body = {
      identifierCode: otpValue,
      brandKey: apiConfig.brandKey,
    };

    apiService
      .loyaltyDiscovery(body)
      .then((response) => {
        const hasFailure = response.notifications?.some((n) => n.severity !== 'Success');
        if (hasFailure) {
          const failureNotification = response.notifications?.find((n) => n.severity !== 'Success');
          setOtpErrorMessage(failureNotification?.text ?? 'This OTP is incorrect, please try again');
          setShowOtpError(true);
        } else {
          if (response.data) {
            dispatch(
              setLoyaltyProfile({
                profileKey: response.data.profileKey,
                profileBrandGroupKey: response.data.profileBrandGroupKey,
                firstName: response.data.firstName,
              })
            );
          }
          dispatch(setLoyaltyCode(otpValue));
          dispatch(setDiningOption('eat-in'));
          setShowOtpModal(false);
          setOtpValue('');
        }
      })
      .catch((err) => {
        setOtpErrorMessage(err?.message ?? 'This OTP is incorrect, please try again');
        setShowOtpError(true);
      })
      .finally(() => {
        setIsSubmittingOtp(false);
      });
  }, [dispatch, otpValue, isSubmittingOtp]);

  return (
    <div className="flex flex-col min-h-screen w-screen bg-[#e9e6df]">
      <div className="relative flex-1 w-full">
        <img
          src={loyaltyImage || LoyaltyImage}
          alt="Loyalty"
          className="absolute inset-0 h-full w-full object-cover"
          draggable={false}
        />
      </div>

      <div className="w-full bg-[#FFFFFF] pt-8 pb-0 px-8 shadow-[0_-8px_16px_rgba(0,0,0,0.12)]">
        <div className="mx-auto flex w-full max-w-[1080px] flex-col items-center justify-between min-h-[260px]">
          <div className="flex w-full justify-between gap-6 px-6">
            <Button
              variant="danger"
              size="xl"
              onClick={handleOpenOtpModal}
              className="w-[420px] rounded-tl-none rounded-tr-[28px] rounded-bl-[28px] rounded-br-none px-[64px] py-[24px] shadow-[0_16px_24px_rgba(0,0,0,0.22)]"
            >
              <span className="flex flex-col items-center leading-tight text-white">
                <span className="uppercase tracking-[2.5px] text-[32px] font-display font-black whitespace-nowrap">Order &amp; Earn</span>
                <span className="mt-1 text-white/95 text-[24px] font-dancing font-medium whitespace-nowrap">Earn point towards vouchers</span>
              </span>
            </Button>

            <Button
              variant="primary"
              size="xl"
              onClick={handleSkipLoyalty}
              className="w-[420px] rounded-tl-none rounded-tr-[28px] rounded-bl-[28px] rounded-br-none px-[64px] py-[24px] shadow-[0_16px_24px_rgba(0,0,0,0.22)]"
            >
              <span className="flex flex-col items-center leading-tight">
                <span className="uppercase tracking-[2.5px] text-[32px] font-display font-black whitespace-nowrap">Skip Loyalty</span>
                <span className="mt-1 text-white/95 text-[24px] font-dancing font-medium whitespace-nowrap">Browse our delicious Menu</span>
              </span>
            </Button>
          </div>

          <p
            className="mt-4 text-center"
            style={{
              fontFamily: '"Museo Sans", ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
              fontSize: '25px',
              color: '#B1B1AF',
            }}
          >
            Earn points towards your Rewards vouchers
          </p>

          <div className="w-full flex justify-center">
            <StartOverButton onClick={handleStartOver} skipConfirmation />
          </div>
        </div>
      </div>

      {showOtpModal && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-white/80 px-6"
          onClick={handleCloseOtpModal}
        >
          <div
            className="relative w-full max-w-[760px] min-h-[950px] rounded-[30px] bg-white shadow-[0_28px_90px_rgba(0,0,0,0.35)] overflow-visible"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={handleCloseOtpModal}
              className="absolute -top-6 -right-6 flex h-20 w-20 items-center justify-center rounded-full bg-white shadow-[0_8px_24px_rgba(0,0,0,0.25)] border-4 border-white"
            >
              <img
                src={CloseIcon}
                alt="Close"
                className="h-8 w-8 object-contain"
                draggable={false}
              />
            </button>

            <div className="flex h-full flex-col items-center px-16 pt-16 pb-12">
              <h2
                className="text-center uppercase"
                style={{
                  fontFamily:
                    '"Nexa Rust Sans Black A", "Museo Sans", ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
                  fontSize: '60px',
                  color: '#00A294',
                  letterSpacing: '3px',
                }}
              >
                Apply Loyalty
              </h2>
              <p
                className="mt-4 text-center"
                style={{
                  fontFamily:
                    '"Museo Sans", ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
                  fontSize: '30px',
                  color: '#3C3C3B',
                }}
              >
                Enter an OTP from the Panarottis Loyalty App
              </p>

              <div
                className={`flex w-full max-w-[600px] items-center justify-between gap-4 ${
                  showOtpError ? 'mt-10' : 'mt-10'
                }`}
              >
                {Array.from({ length: 6 }).map((_, index) => {
                  const value = otpValue[index] ?? '';
                  const isCurrent = index === otpValue.length;
                  const hasValue = value !== '';
                  const borderColor = showOtpError ? '#E32B26' : isCurrent ? '#00A294' : '#D4D4D2';
                  const backgroundColor = hasValue ? '#FFFFFF' : isCurrent ? '#FFFFFF' : '#F1F1F0';
                  const textColor = '#1C1C1C';
                  return (
                    <div
                      key={index}
                      className="flex h-24 w-24 flex-1 items-center justify-center rounded-[22px] border-[3px]"
                      style={{ borderColor, backgroundColor }}
                    >
                      <span
                        style={{
                          fontFamily:
                            '"Nexa Rust Sans Black A", "Museo Sans", ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
                          fontSize: '48px',
                          color: textColor,
                        }}
                      >
                        {value}
                      </span>
                    </div>
                  );
                })}
              </div>

              {(showOtpError || isSubmittingOtp) && (
                <div className="mt-6 flex flex-col items-center justify-center gap-4">
                  {isSubmittingOtp && (
                    <div
                      className="h-10 w-10 animate-spin rounded-full border-4 border-[#00A294] border-t-transparent"
                      aria-hidden
                    />
                  )}
                  {showOtpError && (
                    <p
                      className="text-center"
                      style={{
                        fontFamily:
                          '"Museo Sans", ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
                        fontSize: '30px',
                        color: '#E32B26',
                      }}
                    >
                      {otpErrorMessage || 'This OTP is incorrect, please try again'}
                    </p>
                  )}
                </div>
              )}

              <div className="mt-12 grid w-full max-w-[600px] grid-cols-3 gap-6">
                {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((digit) => (
                  <button
                    key={digit}
                    type="button"
                    onClick={() => handleOtpDigit(digit)}
                    className="flex items-center justify-center rounded-[24px] border-[3px] bg-white shadow-[0_10px_22px_rgba(0,162,148,0.18)]"
                    style={{ borderColor: '#00A294' }}
                  >
                    <span
                      style={{
                        fontFamily:
                          '"Nexa Rust Sans Black A", "Museo Sans", ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
                        fontSize: '60px',
                        color: '#00A294',
                      }}
                    >
                      {digit}
                    </span>
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => handleOtpDigit('0')}
                  className="col-span-2 flex items-center justify-center rounded-[24px] border-[3px] bg-white shadow-[0_10px_22px_rgba(0,162,148,0.18)]"
                  style={{ borderColor: '#00A294' }}
                >
                  <span
                    style={{
                      fontFamily:
                        '"Nexa Rust Sans Black A", "Museo Sans", ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
                      fontSize: '60px',
                      color: '#00A294',
                    }}
                  >
                    0
                  </span>
                </button>
                <button
                  type="button"
                  onClick={handleOtpBackspace}
                  className="flex items-center justify-center rounded-[24px] border-[3px] bg-white shadow-[0_10px_22px_rgba(227,43,38,0.2)]"
                  style={{ borderColor: '#E32B26' }}
                >
                  <img
                    src={BackIcon}
                    alt="Backspace"
                    className="h-16 w-16 object-contain"
                    draggable={false}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoyaltyScreen;

